import { createStyles, FormControlLabel, Grid, makeStyles, Switch, Theme, Typography } from '@material-ui/core';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ChangeEvent, FC, useContext } from 'react';
import { AgentNetDropdownSelector, AgentNetTextInput, CurrencyField } from 'ui-kit/inputs';
import { LOAN_TYPES } from '../constants';
import { ConsiderationFormProps } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerRow: {
      display: 'flex',
      alignItems: 'center',
    },
    description: {
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(4),
      fontFamily: 'Inter',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '150%',
      color: 'var(--text-secondary, #616161)',
    },
    switchLabel: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  }),
);

const ConsiderationForm: FC<ConsiderationFormProps> = ({
  considerationEnabled,
  totalConsideration,
  hasHardMoneyLoan,
  hardMoneyLoanType,
  hardMoneyLoanOtherDescription,
  onConsiderationEnabledChange,
  onTotalConsiderationChange,
  onHasHardMoneyLoanChange,
  onHardMoneyLoanTypeChange,
  onHardMoneyLoanOtherDescriptionChange,
  validationErrors,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { generateButtonClicked, setPaymentValidationErrors } = fincenReportCtx ?? {};

  const getFieldError = (fieldName: string) => {
    if (!generateButtonClicked) return undefined;
    return validationErrors?.find((err) => err.field === fieldName);
  };

  const clearFieldError = (fieldName: string) => {
    if (!validationErrors || validationErrors.length === 0 || !setPaymentValidationErrors) {
      return;
    }
    const filteredErrors = validationErrors.filter((error) => error.field !== fieldName);
    if (filteredErrors.length !== validationErrors.length) {
      setPaymentValidationErrors(filteredErrors);
    }
  };

  const handleHardMoneyLoanTypeChange = (e: ChangeEvent<{ name?: string; value: unknown }>) => {
    clearFieldError('hardMoneyLoanType');
    const selectedValue = e.target.value as string;
    onHardMoneyLoanTypeChange(selectedValue || '');
  };

  const handleTotalConsiderationChange = (e: { target: { value: string | number } }) => {
    clearFieldError('totalConsideration');
    const value = typeof e.target.value === 'number' ? e.target.value : 0;
    onTotalConsiderationChange(value);
  };

  const handleHardMoneyLoanOtherDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    clearFieldError('hardMoneyLoanOtherDescription');
    onHardMoneyLoanOtherDescriptionChange(e.target.value);
  };

  return (
    <>
      <div className={classes.headerRow}>
        <Typography variant="h3">Consideration</Typography>
        <Switch
          size="small"
          checked={considerationEnabled}
          onChange={(_, checked) => onConsiderationEnabledChange(checked)}
          color="primary"
          data-testid="fincen-payment-consideration-enabled-switch"
        />
      </div>
      <Typography className={classes.description} variant="body2">
        {considerationEnabled
          ? 'Disable if there is no paid consideration associated with the transfer, such as in the case of a non-sale transfer.'
          : 'Enable if there is paid consideration associated with the transfer.'}
      </Typography>

      {considerationEnabled && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <CurrencyField
                label="Total Consideration Paid, or to Be Paid, by or on Behalf of This Transferee"
                value={totalConsideration}
                onChange={handleTotalConsiderationChange}
                required
                fullWidth
                error={!!getFieldError('totalConsideration')}
                helperText={getFieldError('totalConsideration')?.message}
                data-testid="fincen-payment-total-consideration-input"
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hasHardMoneyLoan}
                onChange={(_, checked) => onHasHardMoneyLoanChange(checked)}
                color="primary"
                data-testid="fincen-payment-has-hard-money-loan-switch"
              />
            }
            label="Hard money, private, or other similar loans involved in reportable transfer"
            className={classes.switchLabel}
          />

          {hasHardMoneyLoan && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <AgentNetDropdownSelector
                  label="Type of Hard Money, Private, and Other Similar Loans"
                  name="hardMoneyLoanType"
                  value={hardMoneyLoanType || ''}
                  onChange={handleHardMoneyLoanTypeChange}
                  menuOption={(selectedValue) => {
                    clearFieldError('hardMoneyLoanType');
                    onHardMoneyLoanTypeChange(selectedValue || '');
                  }}
                  options={LOAN_TYPES.map((option) => ({
                    name: option.label,
                    value: option.value,
                  }))}
                  required
                  showValidation={!!getFieldError('hardMoneyLoanType')}
                  errs={validationErrors}
                  fullWidth
                  data-testid="fincen-payment-hard-money-loan-type-select"
                />
              </Grid>

              {hardMoneyLoanType === 'OTHER' && (
                <Grid item xs={12} sm={6}>
                  <AgentNetTextInput
                    label="Other Description"
                    value={hardMoneyLoanOtherDescription || ''}
                    onChange={handleHardMoneyLoanOtherDescriptionChange}
                    helperText={getFieldError('hardMoneyLoanOtherDescription')?.message || ''}
                    required
                    fullWidth
                    error={!!getFieldError('hardMoneyLoanOtherDescription')}
                    data-testid="fincen-payment-hard-money-loan-other-description-input"
                  />
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default ConsiderationForm;
